/**
 * Command constants — message types, status maps, category configs, and utility helpers.
 */
import {
  CheckCircle,
  CircleNotch,
  WarningCircle,
  Shield,
  Info,
  MagnifyingGlass,
  Database,
  ArrowsClockwise,
  Sparkle,
  PaperPlaneTilt,
  Plugs,
  TreeStructure,
  Cube,
  PaperPlaneRight,
  CheckSquare,
  FlowArrow,
  EnvelopeSimple,
} from '@phosphor-icons/react';

// ═══════════════════════════════════════════════════════════════
// MESSAGE TYPES
// ═══════════════════════════════════════════════════════════════

export const MSG = {
  USER:          'user',
  ASSISTANT:     'assistant',
  RESULTS:       'results',
  CONFIRMATION:  'confirmation',
  WARNING:       'warning',
  SYSTEM:        'system',
  WELCOME:       'welcome',
  ORCHESTRATION: 'orchestration',
  CREATION:      'creation',
  MODIFICATION:  'modification',
  RUN_EXPLANATION: 'run_explanation',
  CLONE_TEMPLATE: 'clone_template',
  NOTIFICATION_CHANNELS: 'notification_channels',
  CAPABILITY_PLAN: 'capability_plan',
  PLAN_EXECUTION: 'plan_execution',
  BUILDER_STEP: 'builder_step',
  BUILDER_SKILL: 'builder_skill',
};

// ═══════════════════════════════════════════════════════════════
// MESSAGE HELPERS
// ═══════════════════════════════════════════════════════════════

let _msgId = 0;
export const nextId = () => `msg-${++_msgId}-${Date.now()}`;

export const createMessage = (type, content, extra = {}) => ({
  id: nextId(),
  type,
  content,
  timestamp: Date.now(),
  ...extra,
});

export const buildWelcomeMessages = (agentCount, orgName, userRole) => {
  const isMemberRole = userRole === 'member' || userRole === 'employee';
  const msgs = [];
  msgs.push(createMessage(MSG.WELCOME, null, {
    orgName,
    agentCount,
    isMemberRole,
    suggestions: isMemberRole
      ? [
          { label: 'Show my workers', value: 'show my agents' },
          { label: 'Recent activity', value: 'show recent runs' },
          { label: 'Help', value: 'help' },
        ]
      : [
          { label: 'Show my workers', value: 'show my agents' },
          { label: 'Create a worker', value: 'build an agent' },
          { label: 'What can I do?', value: 'help' },
        ],
  }));
  return msgs;
};

// ═══════════════════════════════════════════════════════════════
// STATUS MAPS
// ═══════════════════════════════════════════════════════════════

export const STATUS_ICON_MAP = {
  completed: { icon: CheckCircle, color: '#10B981', weight: 'fill' },
  running: { icon: CircleNotch, color: 'var(--brand-primary)', weight: 'regular', spin: true },
  failed: { icon: WarningCircle, color: '#F87171', weight: 'fill' },
  blocked: { icon: Shield, color: '#F59E0B', weight: 'fill' },
  info: { icon: Info, color: 'var(--brand-primary)', weight: 'fill' },
  unknown: { icon: Info, color: 'var(--text-muted)', weight: 'fill' },
};

export const STATUS_COLOR_MAP = {
  green: '#10B981',
  blue: 'var(--brand-primary)',
  red: '#F87171',
  orange: '#F59E0B',
  yellow: '#F59E0B',
  gray: 'var(--text-muted)',
};

// ═══════════════════════════════════════════════════════════════
// CREATION STEPS
// ═══════════════════════════════════════════════════════════════

export const CREATION_STEPS = [
  { key: 'start', label: 'Analyzing request', icon: 'search' },
  { key: 'intent', label: 'Understanding intent', icon: 'brain' },
  { key: 'workflow', label: 'Building workflow', icon: 'build' },
  { key: 'enhanced', label: 'Enhancing worker', icon: 'sparkle' },
  { key: 'saved', label: 'Saving worker', icon: 'save' },
  { key: 'done', label: 'Complete', icon: 'check' },
];

// ═══════════════════════════════════════════════════════════════
// CLONE PHASES
// ═══════════════════════════════════════════════════════════════

export const CLONE_PHASES = {
  parsing: { label: 'Interpreting request...', icon: CircleNotch, spin: true, color: 'var(--brand-primary)' },
  source_found: { label: 'Source worker found', icon: MagnifyingGlass, color: '#10B981' },
  diffing: { label: 'Applying modifications...', icon: CircleNotch, spin: true, color: 'var(--brand-primary)' },
  created: { label: 'Worker created', icon: CheckCircle, color: '#10B981' },
  done: { label: 'Clone complete', icon: CheckCircle, color: '#10B981' },
  error: { label: 'Clone failed', icon: WarningCircle, color: '#F87171' },
};

// ═══════════════════════════════════════════════════════════════
// CAPABILITY PLAN COLORS/ICONS
// ═══════════════════════════════════════════════════════════════

export const CATEGORY_COLORS = {
  ingest: '#3B82F6',
  transform: '#8B5CF6',
  analyze: '#06B6D4',
  generate: '#10B981',
  deliver: '#F59E0B',
  integrate: '#EC4899',
  orchestrate: '#F97316',
  unknown: '#6B7280',
};

export const CATEGORY_ICONS = {
  ingest: Database,
  transform: ArrowsClockwise,
  analyze: MagnifyingGlass,
  generate: Sparkle,
  deliver: PaperPlaneTilt,
  integrate: Plugs,
  orchestrate: TreeStructure,
  unknown: Cube,
};

// ═══════════════════════════════════════════════════════════════
// PLAN EXECUTION STATUS STYLES
// ═══════════════════════════════════════════════════════════════

export const EXEC_STATUS_STYLES = {
  pending: { color: 'var(--text-muted)', bg: 'var(--surface-3)', label: 'Pending' },
  in_progress: { color: '#3B82F6', bg: 'rgba(59,130,246,0.08)', label: 'Running' },
  complete: { color: '#10B981', bg: 'rgba(16,185,129,0.08)', label: 'Done' },
  failed: { color: '#EF4444', bg: 'rgba(239,68,68,0.08)', label: 'Failed' },
  skipped: { color: '#6B7280', bg: 'rgba(107,114,128,0.08)', label: 'Skipped' },
  approval_needed: { color: '#F59E0B', bg: 'rgba(245,158,11,0.08)', label: 'Needs Approval' },
};

// ═══════════════════════════════════════════════════════════════
// NOTIFICATION CHANNEL ICONS
// ═══════════════════════════════════════════════════════════════

export const CHANNEL_ICONS = {
  slack: { icon: Plugs, color: '#E01E5A' },
  webhook: { icon: FlowArrow, color: '#6366F1' },
  email: { icon: EnvelopeSimple, color: '#F59E0B' },
};

// ═══════════════════════════════════════════════════════════════
// QUICK ACTION CATALOG
// ═══════════════════════════════════════════════════════════════

export const QUICK_ACTION_CATALOG = [
  { id: 'validation', label: 'Add validation', icon: Shield, keywords: ['validate', 'validation', 'check'] },
  { id: 'email', label: 'Add email notification', icon: PaperPlaneRight, keywords: ['email', 'notification', 'notify'] },
  { id: 'retry', label: 'Add retry logic', icon: ArrowsClockwise, keywords: ['retry', 'error_handler'] },
  { id: 'approval', label: 'Add approval step', icon: CheckSquare, keywords: ['approval', 'review', 'approve'] },
  { id: 'output', label: 'Replace output format', icon: Database, keywords: ['output', 'format', 'response'] },
  { id: 'condition', label: 'Add conditional logic', icon: FlowArrow, keywords: ['condition', 'if', 'branch'] },
];
